// Remaining application views: admin, reports, self-service,
// salesforce, lawyer workbench, metrics, custom dashboards.
//
// This file holds the bulk of the per-role landing pages and
// admin-only management UIs. Future sessions may split this further
// (e.g. admin/, reports/) but for now they live together as the
// last extraction batch before the App entry point.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { T } from "../theme";
import { fmtDate, today, capitalize } from "../utils";
import { apiCall } from "../apiClient";
import { useClientScope } from "../contexts/ClientScopeContext";
import { DEPARTMENTS, STATUS_COLORS } from "../constants";
import { SF_INSTANCE, SF_OPPORTUNITIES, SF_STAGE_COLORS, sfLink } from "../salesforce";
import { renderMarkdown } from "./markdown";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";
import { DashboardView } from "./Dashboards";
import { ExtractionDashView, SecurityView, ValidationView } from "./AiViews";
import { SsoProvidersView } from "./SsoProvidersView";

export const LawyerWorkbench = ({ requests = [], user = null, onSelect = undefined, onNav = undefined }: { requests?: RequestRow[]; user?: UserRow | null; onSelect?: any; onNav?: any }) => {
  const myId = user?.lawyerId || user?.id;
  const mine = requests.filter((r: RequestRow) => r.assignedTo === myId);
  const today = new Date().toISOString().split('T')[0];
  const in7days = (() => { const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString().split('T')[0]; })();

  const active = mine.filter((r: RequestRow) => !["Fully Executed", "Cancelled"].includes(r.status || ''));
  const dueToday = active.filter((r: RequestRow) => r.targetDate === today);
  const dueThisWeek = active.filter((r: RequestRow) => r.targetDate && r.targetDate > today && r.targetDate <= in7days);
  const overdue = active.filter((r: RequestRow) => r.targetDate && r.targetDate < today);
  const inMyReview = mine.filter((r: RequestRow) => r.status === "Legal Review");
  const waitingBusiness = mine.filter((r: RequestRow) => r.status === "Business Review");
  const waitingCounterparty = mine.filter((r: RequestRow) => r.status === "Counterparty Review");
  const readyForExecution = mine.filter((r: RequestRow) => ["Approved for Signature", "Sent for Signature"].includes(r.status || ''));
  const needsMoreInfo = mine.filter((r: RequestRow) => r.status === "Needs More Information");

  const [selectedList, setSelectedList] = useState<any>(null);
  const [listTitle, setListTitle] = useState("");

  const showList = (items: any[], title: string) => { setSelectedList(items); setListTitle(title); };

  const StatCard = ({ label, count, color, items }: { label?: string; count?: number; color?: string; items?: any[] }) => (
    <div onClick={() => (items || []).length > 0 && showList(items || [], label || "")} style={{ padding: 16, backgroundColor: T.bg3, borderRadius: 8, cursor: (items || []).length > 0 ? "pointer" : "default", borderLeft: `3px solid ${color}`, transition: "all .15s" }}>
      <div style={{ fontSize: 24, fontWeight: 800, color }}>{count}</div>
      <div style={{ fontSize: 11, color: T.txM, marginTop: 2 }}>{label}</div>
    </div>
  );

  return (
    <div>
      <SectionTitle sub={`${active.length} active matters assigned to you`}>My Workbench</SectionTitle>

      {/* Priority row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        <StatCard label="Overdue" count={overdue.length} color={T.red} items={overdue} />
        <StatCard label="Due Today" count={dueToday.length} color={T.orn} items={dueToday} />
        <StatCard label="Due This Week" count={dueThisWeek.length} color={T.ylw} items={dueThisWeek} />
        <StatCard label="Total Active" count={active.length} color={T.acc} items={active} />
      </div>

      {/* Status row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 20 }}>
        <StatCard label="In My Review" count={inMyReview.length} color={T.blu} items={inMyReview} />
        <StatCard label="Business Review" count={waitingBusiness.length} color={T.prp} items={waitingBusiness} />
        <StatCard label="Counterparty Review" count={waitingCounterparty.length} color={T.cyn} items={waitingCounterparty} />
        <StatCard label="Ready for Execution" count={readyForExecution.length} color={T.grn} items={readyForExecution} />
        <StatCard label="Needs Info" count={needsMoreInfo.length} color={T.red} items={needsMoreInfo} />
      </div>

      {/* Selected list or default: overdue + due today */}
      <Card>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>{listTitle || (overdue.length > 0 ? "Overdue Matters" : "Due This Week")}</div>
        {(selectedList || (overdue.length > 0 ? overdue : dueThisWeek.length > 0 ? dueThisWeek : active.slice(0, 10))).map((r: any) => (
          <div key={r.id} onClick={() => onSelect && onSelect?.(r)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${T.brdSubtle}`, cursor: "pointer" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.tx }}>{r.title}</div>
              <div style={{ fontSize: 11, color: T.txM }}>{r.counterparty} · {r.type}</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {r.targetDate && <span style={{ fontSize: 10, color: r.targetDate < today ? T.red : T.txD }}>{fmtDate(r.targetDate)}</span>}
              <Badge c={STATUS_COLORS[r.status || '']?.c || T.txM} b={STATUS_COLORS[r.status || '']?.b || T.bg3}>{r.status}</Badge>
            </div>
          </div>
        ))}
        {(selectedList || overdue).length === 0 && active.length === 0 && <div style={{ fontSize: 12, color: T.txM, padding: "16px 0", textAlign: "center" }}>No active matters assigned to you</div>}
      </Card>
    </div>
  );
};
